import { reduce, find, each, round, sumBy, isFinite, isNumber, get, times, indexOf } from 'lodash';

/**
 * Service that prepares sum values for tables
 */
function Table($filter, $uiRouterGlobals, $document) {
  'ngInject';
  /**
   * @ngdoc property
   * @name _cache
   * @propertyOf adjust.service:Table
   *
   * @description
   * Hash that holds average and sum values
   *
   * @type {Object}
   * @private
   */
  var _cache = {};
  var _cacheReduced = {};
  var _abbreviateAbove = [1000000000, 'B'];
  const _organicName = 'Organic';
  const _udName = 'Untrusted Devices';

  return {
    organicName: _organicName,
    udName: _udName,
    prepareColumns: prepareColumns,
    prepareRows: prepareRows,
    getTotals: getTotals,
    getTotal: getTotal,
    getSum: getSum,
    getSumFormatted: getSumFormatted,
    getWeightedAverage: getWeightedAverage,
    getRejectedRate: getRejectedRate,
    format: format,
    getFormat: getFormat,
    isPercent: isPercent,
    isPrice: isPrice,
    buildOverviewTable: buildOverviewTable,
    buildCohortsTable: buildCohortsTable,
    buildAppsTable: buildAppsTable,
    toggleRow: toggleRow,
    hidePinned: hidePinned,
    getWeightedPair: getWeightedPair,
    calculateWeightedAverage: calculateWeightedAverage,
    getRejectedRateSet: getRejectedRateSet,
  };

  /**
   * @ngdoc method
   * @name _reduceTable
   * @methodOf adjust.service:Table
   *
   * @description
   * Calculate sum of non-null values in the array
   *
   * @param {Array} array array to be reduced
   * @param {number} index index of the actual value
   * @returns {Object} reduced object with sum
   * @private
   */
  function _reduceTable(array, index) {
    var initial = { sum: 0 };

    return reduce(array, function(tmp, o) {
      var value;

      value = o.kpi_values[index];

      var final =
        value && !o.aggregate
          ? {
              sum: tmp.sum + value,
            }
          : tmp;

      return final;
    }, initial);
  }

  /**
   * @ngdoc method
   * @name _preserve
   * @methodOf adjust.service:Table
   *
   * @description
   * Preserve the cache value
   *
   * @param {object} value reduced value
   * @param {string} key cache key to be stored
   * @private
   */
  function _preserve(value, key) {
    _cache[key] = {
      sum: value.sum,
    };
  }

  /**
   * @ngdoc method
   * @name prepareColumns
   * @methodOf adjust.service:Table
   *
   * @description
   * Prepare sum and average for table columns
   *
   * @param {array} array array for which sum values are prepared
   * @param {array} keys columns for which are sums calculated
   * @param {string} subLevel sub-level key name
   */
  function prepareColumns(array, keys) {
    array = array || [];

    const organicTracker = find(array, t => [_organicName, '00000::Organic'].includes(t.name));
    const udTracker = find(array, { name: _udName });

    _cache = {};
    _cacheReduced = { organic: {}, ud: {} };

    each(keys, function(key, index) {
      const reduced = _reduceTable(array, index);

      if (['installs', 'rejected_installs', 'reattributions', 'rejected_reattributions'].includes(key)) {
        _cacheReduced.organic[key] = organicTracker ? organicTracker.kpi_values[index] : 0;
        _cacheReduced.ud[key] = udTracker ? udTracker.kpi_values[index] : 0;
      }

      _preserve(reduced, key);
    });
  }

  /**
   * @ngdoc method
   * @name prepareRows
   * @methodOf adjust.service:Table
   *
   * @description
   * Prepare sum and average for table rows
   *
   * @param {array} array array for which sum values are prepared
   * @returns {Object} Hash with sum values
   */
  function prepareRows(array) {
    array = array || [];

    _cache = {};

    each(array, function(item) {
      var reduced = _reduceTable(item.periods, item.name);

      _preserve(reduced, item.name);
    });
  }

  /**
   * @ngdoc method
   * @name getTotals
   * @methodOf adjust.service:Table
   *
   * @description
   * Get table total values
   *
   * @returns {Object} total values for each column
   */
  function getTotals() {
    return angular.copy(_cache);
  }

  /**
   * @ngdoc method
   * @name getTotal
   * @methodOf adjust.service:Table
   *
   * @description
   * Get total value for particular column
   *
   * @param {Object} options options to get the total
   * @param {string} options.kpi kpi to get total for
   * @param {Array} options.trackers list of trackers used for wighted calculation
   * @param {Array} options.kpiNames list of kpi names used for wighted calculation
   * @param {Object=} options.currency currency used for formatting
   * @param {boolean=false} options.csv if total is displayed in csv report
   * @param {allowShort=false} options.allowShort Abbreviate huge numbers to shorter form
   *
   * @returns {string|number} total value
   */
  function getTotal(options) {
    var total;
    var weightedPair = getWeightedPair(options.kpi);
    var rejectedRateSet = getRejectedRateSet(options.kpi);

    options.allowShort = options.allowShort || false;

    if (weightedPair) {
      total = getWeightedAverage(options.trackers, options.kpiNames, weightedPair);
      return options.csv
        ? round(total, 4)
        : format(total, options.kpi, options.currency, {
            allowShort: options.allowShort,
          });
    }

    if (rejectedRateSet) {
      total = getRejectedRate(rejectedRateSet);
      return options.csv
        ? round(total, 4)
        : format(total, rejectedRateSet.rateKey, options.currency, {
            allowShort: options.allowShort,
          });
    }

    total = _getSumRounded(options.kpi);

    return options.csv
      ? total
      : format(total, options.kpi, options.currency, {
          allowShort: options.allowShort,
        });
  }

  /**
   * @ngdoc method
   * @name getSum
   * @methodOf adjust.service:Table
   *
   * @description
   * Get sum value
   *
   * @param {string} key column key
   * @returns {number} Sum value for particular column
   */
  function getSum(key) {
    return _cache && _cache[key] ? _cache[key].sum : null;
  }

  /**
   * @ngdoc method
   * @name _getSumRounded
   * @methodOf adjust.service:Table
   *
   * @description
   * Get rounded sum for particular key
   *
   * @param {string} key column key
   * @returns {number} Rounded sum value for particular column
   * @private
   */
  function _getSumRounded(key) {
    var sum = getSum(key);
    var toRound = ['daus', 'waus', 'maus'];

    return toRound.includes(key) ? Math.round(sum) : sum;
  }

  /**
   * @ngdoc method
   * @name getSumFormatted
   * @methodOf adjust.service:Table
   *
   * @description
   * Get formatted rounded sum for particular key
   *
   * @param {string} key column key
   * @param {Object=} currency currency symbol and name
   * @returns {string} formatted sum value
   */
  function getSumFormatted(key, currency) {
    var sum = _getSumRounded(key);

    return format(sum, key, currency);
  }

  /**
   * Get weighted average sum for particular keys (`clicks` and `click_conversion_rate` for example)
   *
   * @param {Array} array array for which the weighted average is calculated
   * @param {Array} kpis list of kpi names that corresponds to kpi_values positions
   * @param {Object} weightedPair weighted pair
   * @param {Boolean=} perDay marks if summing up all or just particular day
   * @returns {Number} weighted average
   */
  function getWeightedAverage(array, kpis, weightedPair, perDay) {
    const [divisorKpi, dividendKpi] = weightedPair.pairs;

    const divisorIdx = indexOf(kpis, divisorKpi);
    const dividendIdx = indexOf(kpis, dividendKpi);

    const divisorSum = perDay ? _calculateSum(array, divisorIdx) : getSum(divisorKpi);
    const dividendSum = perDay ? _calculateSum(array, dividendIdx) : getSum(dividendKpi);

    return calculateWeightedAverage([divisorSum, dividendSum], weightedPair.perMil);
  }

  /**
   * Calculate weighted average for particular keys (`clicks` and `click_conversion_rate` for example)
   *
   * @param {Array} pair
   * @param {Boolean} isPerMil
   * @returns {Number}
   */
  function calculateWeightedAverage(pair, isPerMil) {
    const multiplicand = isPerMil ? 1000 : 1;
    const [divisor, dividend] = pair;

    if (divisor === 0 || !Number.isFinite(divisor) ||
        dividend === 0 || !Number.isFinite(dividend)) {
      return 0;
    }

    return (divisor / dividend) * multiplicand;
  }

  /**
   * @ngdoc method
   * @name _getSumWithoutOrganicAndUd
   * @methodOf adjust.service:Table
   *
   * @description
   * Get the sum for particular kpi but without organic and untrusted devices included
   *
   * @param {string} kpi kpi for which the sum is calculated
   * @returns {number} sum without organic and untrusted devices
   * @private
   */
  function _getSumWithoutOrganicAndUd(kpi) {
    var removeValue = _cacheReduced.organic[kpi] + _cacheReduced.ud[kpi];

    return _cache[kpi] && _cache[kpi].sum ? _cache[kpi].sum - removeValue : 0;
  }

  /**
   * Get rejected rate total value for particular kpi (install, reattribution)
   *
   * @param {Object} set
   * @param {String} set.rejectedKey
   * @param {String} set.totalKey
   * @param {Array=} values
   * @returns {Number}
   */
  function getRejectedRate(set, values) {
    var rejectedSum = values ? _calculateSum(values, 2) : _getSumWithoutOrganicAndUd(set.rejectedKey);
    var totalSum = values ? _calculateSum(values, 1) : _getSumWithoutOrganicAndUd(set.totalKey);
    var sum = rejectedSum + totalSum;

    if (!sum) {
      return 0;
    }

    return rejectedSum / sum;
  }

  /**
   * @ngdoc method
   * @name _calculateSum
   * @methodOf adjust.service:Table
   *
   * @description
   * Calculate sum for particular kpi
   *
   * @param {Array} array array for which the sum is calculated
   * @param {number} index kpi index
   * @returns {number} sum of kpi values
   * @private
   */
  function _calculateSum(array = [], index) {
    return sumBy(array, item => item.kpi_values[index]);
  }

  /**
   * @ngdoc method
   * @name format
   * @methodOf adjust.service:Table
   *
   * @description
   * Format provided valued depending on the kpi
   *
   * @param {number} value value to be formatted
   * @param {string} kpiName kpi which value represent or explicit filter with structure `filter:{filterName}`
   * @param {Object=} currency currency symbol and name
   * @param {boolean=} options.allowZero flag if we should display 0 instead of *n/a*
   * @param {number=2} options.round number of decimal places
   * @param {boolean=false} options.allowShort Abbreviate huge numbers
   * @returns {string} Formatted value
   */
  function format(value, kpiName, currency, options) {
    if (_ignoreForSandbox(kpiName)) {
      return 'sandbox';
    }

    if (!isFinite(value)) {
      return 'n/a';
    }

    options = options || {};

    var allowZero = options.allowZero || false;
    var round = isNumber(options.round) ? options.round : 2;
    var shortAbove = options.allowShort ? _abbreviateAbove[1] : false;

    var filter = getFormat(kpiName, currency);
    var filterArgs = [];

    if (filter.name === 'percent') {
      allowZero = false;
      filterArgs = [allowZero, round];
    }

    if (filter.name === 'number' && filter.explicit) {
      filterArgs = [2, shortAbove];
      if (options.allowShort) {
        filter.name = 'shortNumber';
      }
    }

    if (filter.name === 'currency') {
      filterArgs = [currency.symbol, round, shortAbove];
      if (options.allowShort) {
        filter.name = 'shortCurrency';
      }
    } else if (!filterArgs.length) {
      filterArgs = [allowZero];
    }

    if (filter.name === 'round' && options.allowShort) {
      filterArgs.push(shortAbove);
    }

    filterArgs.unshift(value);

    return $filter(filter.name).apply(this, filterArgs);
  }

  /**
   * @ngdoc method
   * @name getFormat
   * @methodOf adjust.service:Table
   *
   * @description
   * Get the format for particular key
   *
   * @param {string} key key for which we want format
   * @param {Object} currency currency details
   * @returns {Object} format information
   */
  function getFormat(key, currency) {
    var format = 'round';
    var explicit = false;

    if (key?.includes('filter:')) {
      var [, givenFormat] = key.split('filter:');
      format = givenFormat;
      explicit = true;
    } else {
      if (isPercent(key)) {
        format = 'percent';
      } else if (isPrice(key) && currency) {
        format = 'currency';
      }
    }

    return { name: format, explicit: explicit };
  }

  /**
   * @ngdoc method
   * @name isPercent
   * @methodOf adjust.service:Table
   *
   * @description
   * Check if key should be formatted as percent
   *
   * @param {string} key key to check
   * @returns {boolean}
   */
  function isPercent(key) {
    if (key?.includes('_rate')) {
      return true;
    }

    return [
      'ctr',
      'return_on_investment',
      'rcr',
      'retention_rate',
      'paying_users_retention_rate',
      'conversion_distribution',
      'roas',
    ].includes(key);
  }

  /**
   * @ngdoc method
   * @name isPrice
   * @methodOf adjust.service:Table
   *
   * @description
   * Check if key is price like in order to format it properly
   *
   * @param {string} key key to check
   * @returns {boolean} is price like
   */
  function isPrice(key) {
    if (/_revenue$/.test(key)) {
      return true;
    }

    return [
      'revenue',
      'revenue_per_user',
      'revenue_total',
      'revenue_per_paying_user',
      'ecpi',
      'cohort_gross_profit',
      'lifetime_value',
      'paying_user_lifetime_value',
      'cost',
      'ecpc',
      'ecpm',
      'ad_revenue',
      'ad_rpm',
      'cohort_ad_revenue',
      'cohort_all_revenue',
      'all_revenue',
    ].includes(key);
  }

  /**
   * @ngdoc method
   * @name _ignoreForSandbox
   * @methodOf adjust.service:Table
   *
   * @description
   * Check if key should be ignored if in sandbox mode
   *
   * @param {string} key kpi name
   * @returns {boolean} should be ignored or not
   * @private
   */
  function _ignoreForSandbox(key) {
    var paramsToIgnore = ['clicks', 'click_conversion_rate', 'impression_conversion_rate'];
    var sandboxActive = $uiRouterGlobals.params.sandbox === 'true' || false;

    return sandboxActive && paramsToIgnore.includes(key);
  }

  /**
   * @ngdoc method
   * @name _buildCell
   * @methodOf adjust.service:Table
   *
   * @description
   * Render table cell markup
   *
   * @param {string=} cls element class
   * @param {string=} value element title and node value
   * @param {string=} shortValue short node value
   * @returns {string} cell markup
   * @private
   */
  function _buildCell(cls, value, shortValue) {
    var val = shortValue || value;

    return `<div class="td ${cls}" title="${value}">${val}</div>`;
  }

  /**
   * @ngdoc method
   * @name _buildRow
   * @methodOf adjust.service:Table
   *
   * @description
   * Render table row markup
   *
   * @param {Array} row array of cells in the row
   * @param {boolean=} withIds render row with id
   * @param {Object=} tracker tracker object
   * @returns {string} row markup
   * @private
   */
  function _buildRow(row, withIds, tracker) {
    var id = withIds ? `id="tr-value-${tracker.token}"` : '';
    var totalCls = tracker && tracker.aggregate ? 'aggregate' : '';
    var emptyCls = tracker && tracker.token === 'empty' ? 'tr-empty' : '';
    var classes = ['tr', 'tr-body', totalCls, emptyCls].join(' ').trim();

    return `<div ${id} class="${classes}">${row.join('')}</div>`;
  }

  /**
   * @ngdoc method
   * @name _getColorClass
   * @methodOf adjust.service:Table
   *
   * @description
   * Get color class name depending on the value compared to total
   *
   * @returns {string} color class
   * @private
   */
  function _getColorClass(value, key) {
    if (!value || _ignoreForSandbox(key)) {
      return 'color-0';
    }

    return '';
  }

  /**
   * @ngdoc method
   * @name buildOverviewTable
   * @methodOf adjust.service:Table
   *
   * @description
   * Build overview table markup
   *
   * @param {Object} options various options in order to build table
   * @param {Array} options.trackers trackers list
   * @param {Array} options.events events list
   * @param {Object} options.currency currency details
   * @param {boolean} options.withIds render rows with ids
   * @returns {string} table markup
   */
  function buildOverviewTable(options) {
    var table = [];

    each(options.trackers, function(tracker) {
      var row = [];

      each(options.events, function(event, i) {
        var index = get(event, 'idx', i);
        var value = tracker.kpi_values[index];
        var formattedValue = format(value, event.token, options.currency, {
          allowZero: true,
        });
        var formattedValueShort =
          value >= _abbreviateAbove[0]
            ? format(value, event.token, options.currency, {
                allowZero: true,
                allowShort: true,
              })
            : formattedValue;

        var cls = _getColorClass(value, event.token) + (event.empty || event.hide ? ' hide' : '');
        var cell = _buildCell(cls, formattedValue, formattedValueShort);

        row.push(cell);
      });

      table.push(_buildRow(row, options.withIds, tracker));
    });

    if (!options.trackers) {
      var row = times(options.events.length, _buildCell);

      table.push(_buildRow(row, false, { token: 'empty' }));
    }

    return table.join('');
  }

  /**
   * @ngdoc method
   * @name buildCohortsTable
   * @methodOf adjust.service:Table
   *
   * @description
   * Build cohorts table markup
   *
   * @param {Object} options various options in order to build table
   * @param {Array} options.trackers trackers list
   * @param {Array} options.periods periods list
   * @param {Array} options.kpiNames kpis list
   * @param {Object} options.currency currency details
   * @param {boolean} options.withIds render rows with ids
   * @returns {string} table markup
   */
  function buildCohortsTable(options) {
    var table = [];

    var getValue = function(tracker, period, index) {
      var value = find(tracker.periods, { period: String(period) });

      return value ? value.kpi_values[index] : 0;
    };

    function formatValue(value, kpi, allowShort) {
      var formatted = format(value, 'filter:' + (kpi.format || 'round'), options.currency, {
        allowShort: allowShort,
      });

      return formatted === 'n/a' ? '-' : formatted;
    }

    each(options.trackers, function(tracker) {
      var row = [];

      each(options.periods, function(period) {
        each(options.kpiNames, function(kpi, index) {
          var value = getValue(tracker, period, index);
          var formattedValue = formatValue(value, kpi);
          var formatedValueShort = value >= _abbreviateAbove[0] ? formatValue(value, kpi, true) : formattedValue;
          var cls = _getColorClass(value, tracker.name);
          var cell = _buildCell(cls, formattedValue, formatedValueShort);

          row.push(cell);
        });
      });

      table.push(_buildRow(row, options.withIds, tracker));
    });

    if (!options.trackers) {
      var row = times(options.periods.length + options.kpiNames.length, _buildCell);

      table.push(_buildRow(row, false, { token: 'empty' }));
    }

    return table.join('');
  }

  /**
   * @ngdoc method
   * @name buildAppsTable
   * @methodOf adjust.service:Table
   *
   * @description
   * Build apps table markup
   *
   * @param {Object} options various options in order to build table
   * @param {Array} options.apps apps list
   * @param {Array} options.kpiNames kpis to display
   * @returns {string} table markup
   */
  function buildAppsTable(options) {
    var table = [];

    each(options.apps, function(app) {
      var row = [];

      each(options.kpiNames, function(kpiName, index) {
        var value = app.kpi_values[index];
        var formattedValue = format(value, kpiName, app.currency);
        var formattedValueShort =
          value >= _abbreviateAbove[0]
            ? format(value, kpiName, app.currency, {
                allowShort: true,
              })
            : formattedValue;
        var cls = _getColorClass(value, kpiName);
        var cell = _buildCell(cls, formattedValue, formattedValueShort);

        row.push(cell);
      });

      table.push(_buildRow(row));
    });

    return table.join('');
  }

  /**
   * @ngdoc method
   * @name toggleRow
   * @methodOf adjust.service:Table
   *
   * @description
   * Toggle row visibility
   *
   * @param {string} token part of the id
   * @param {string} action hide or show the row
   */
  function toggleRow(token, action) {
    var column = $document[0].querySelector('#tr-column-' + token);
    var value = $document[0].querySelector('#tr-value-' + token);

    if (column && value) {
      column.classList[action]('hidden');
      value.classList[action]('hidden');
    }
  }

  /**
   * @ngdoc method
   * @name hidePinned
   * @methodOf adjust.service:Table
   *
   * @description
   * Hide pinned trackers in the main list
   *
   * @param {Array} pinned array of pinned trackers
   */
  function hidePinned(pinned) {
    var hidden = $document[0].querySelectorAll('.tr-body.hidden');

    each(hidden, function(el) {
      el.classList.remove('hidden');
    });

    each(pinned, function(token) {
      toggleRow(token, 'add');
    });
  }

  /**
   * Get rate pair for particular kpi key
   *
   * @param {string} key kpi key
   * @returns {Object} rate pair values
   */
  function getWeightedPair(key) {
    const pairs = {
      click_conversion_rate: ['installs', 'clicks'],
      ctr: ['clicks', 'impressions'],
      impression_conversion_rate: ['installs', 'impressions'],
      ecpc: ['cost', 'paid_clicks'],
      ecpi: ['cost', 'paid_installs'],
      return_on_investment: ['cohort_gross_profit', 'cost'],
      limit_ad_tracking_install_rate: ['limit_ad_tracking_installs', 'installs'],
      ecpm: ['cost', 'paid_impressions'],
      rcr: ['cohort_all_revenue', 'cost'],
      ad_rpm: ['ad_revenue', 'ad_impressions'],
      roas: ['cohort_all_revenue', 'cost'],
    };
    const perMil = ['ecpm', 'ad_rpm'];

    return pairs[key]
      ? {
          pairs: pairs[key],
          perMil: perMil.includes(key),
        }
      : null;
  }

  /**
   * Get the list of related keys needed to be calculated for particular rejected rate kpi
   *
   * @param {String} key
   * @returns {object|Null}
   */
  function getRejectedRateSet(key) {
    var pairs = {
      rejected_install_rate: {
        total: 'installs',
        rejected: 'rejected_installs',
      },
      rejected_reattribution_rate: {
        total: 'reattributions',
        rejected: 'rejected_reattributions',
      },
    };

    if (pairs[key]) {
      return {
        rateKey: key,
        totalKey: pairs[key].total,
        rejectedKey: pairs[key].rejected,
      };
    }

    return null;
  }
}

export { Table };
